import { DocumentGroup, DocumentResponse } from "@/api";
import { QueryKey } from "@/query/Query";

export interface DragData {
  file: DocumentResponse;
  sourceZone: DocumentGroup;
}

/**
 * A drag touching an asset inventory upload area that has not been persisted yet — between the
 * dropzone panel and an area, or between two different areas. At least one of source/target is
 * an asset inventory zone. Persisted on the owning form's save, discarded on reset.
 */
export interface PendingDocumentMove {
  document: DocumentResponse;
  source: DocumentGroup;
  target: DocumentGroup;
}

/**
 * Single source of truth for the asset inventory upload area zones: each zone paired with the
 * react-query key root of its document list ([key, clientId]). Everything else derives from
 * this record — the zone set, the cross-zone invalidations (DropZoneDnd.utils.ts), the
 * ALLOWED_DROPS matrix below, and the upload-area registry (the asset-inventory-tab's
 * assetInventoryUploadAreas.ts, typed against this record). When adding a new upload area, add
 * its entry here and follow the compile errors; only the zone label case in
 * DocumentDndProvider.getZoneName is enforced separately (by the DocumentGroup switch).
 */
export const ASSET_INVENTORY_ZONE_QUERY_KEYS = {
  [DocumentGroup.AssetInventoryDependants]: QueryKey.AssetInventoryDependantDocuments,
  [DocumentGroup.AssetInventoryBankStatement]: QueryKey.AssetInventoryBankStatements,
  [DocumentGroup.AssetInventoryIncome]: QueryKey.AssetInventoryIncomeProofs,
  [DocumentGroup.AssetInventoryExpenses]: QueryKey.AssetInventoryExpenseProofs,
  [DocumentGroup.AssetInventoryOwnClaims]: QueryKey.AssetInventoryOwnClaimDocuments,
  [DocumentGroup.AssetInventoryDomesticRealEstate]: QueryKey.AssetInventoryDomesticRealEstateDocuments,
  [DocumentGroup.AssetInventoryForeignRealEstate]: QueryKey.AssetInventoryForeignRealEstateDocuments,
  [DocumentGroup.AssetInventoryBuildingSavingsContracts]: QueryKey.AssetInventoryBuildingSavingsContractDocuments,
  [DocumentGroup.AssetInventorySavingsAccounts]: QueryKey.AssetInventorySavingsAccountDocuments,
  [DocumentGroup.AssetInventoryLifeInsurances]: QueryKey.AssetInventoryLifeInsuranceDocuments,
  [DocumentGroup.AssetInventoryPensionInsurances]: QueryKey.AssetInventoryPensionInsuranceDocuments,
  [DocumentGroup.AssetInventorySecuritiesDepots]: QueryKey.AssetInventorySecuritiesDepotDocuments,
  [DocumentGroup.AssetInventoryCashAndBankAssets]: QueryKey.AssetInventoryCashAndBankAssetDocuments,
  [DocumentGroup.AssetInventoryVehicles]: QueryKey.AssetInventoryVehicleDocuments,
  [DocumentGroup.AssetInventoryBuildingsOnForeignLand]: QueryKey.AssetInventoryBuildingsOnForeignLandDocuments,
  [DocumentGroup.AssetInventoryTenancyRights]: QueryKey.AssetInventoryTenancyRightDocuments,
  [DocumentGroup.AssetInventoryCompanyShares]: QueryKey.AssetInventoryCompanyShareDocuments,
  [DocumentGroup.AssetInventoryCooperativeMemberships]: QueryKey.AssetInventoryCooperativeMembershipDocuments,
  [DocumentGroup.AssetInventoryOtherPropertyRights]: QueryKey.AssetInventoryOtherPropertyRightDocuments,
  [DocumentGroup.AssetInventoryDonations]: QueryKey.AssetInventoryDonationDocuments,
} as const satisfies Partial<Record<DocumentGroup, QueryKey>>;

/** The DocumentGroups that are asset inventory upload area zones. */
export type AssetInventoryZone = keyof typeof ASSET_INVENTORY_ZONE_QUERY_KEYS;

const assetInventoryZones = Object.keys(ASSET_INVENTORY_ZONE_QUERY_KEYS) as AssetInventoryZone[];

function otherAssetInventoryZones(zone: DocumentGroup): DocumentGroup[] {
  return assetInventoryZones.filter((other) => other !== zone);
}

export function isAssetInventoryZone(zone: DocumentGroup): zone is AssetInventoryZone {
  return zone in ASSET_INVENTORY_ZONE_QUERY_KEYS;
}

/** The pending moves whose files should show up as pending rows in the given zone. */
export function getIncomingMoves(moves: PendingDocumentMove[], zone: DocumentGroup): PendingDocumentMove[] {
  return moves.filter((move) => move.target === zone);
}

/** Ids of documents that are pending a move out of the given zone and should be hidden there. */
export function getOutgoingDocumentIds(moves: PendingDocumentMove[], zone: DocumentGroup): Set<string> {
  return new Set(moves.filter((move) => move.source === zone).map((move) => move.document.documentId));
}

const ASSET_INVENTORY_DROPS = Object.fromEntries(
  assetInventoryZones.map((zone) => [
    zone,
    new Set([DocumentGroup.Client, DocumentGroup.Counselor, ...otherAssetInventoryZones(zone)]),
  ])
) as Record<AssetInventoryZone, Set<DocumentGroup>>;

/**
 * Allowed drag-and-drop transitions between groups.
 *
 * From / To                | Counselor | Client | ClientSelf | CaseNote | AssetInventory*
 * --------------------------|-----------|--------|------------|----------|----------------
 * Counselor                 |     -     |  yes   |     no     |    no    |       yes
 * Client                    |    yes    |   -    |     no     |    no    |       yes
 * ClientSelf                |    yes    |  yes   |      -     |    no    |       yes
 * CaseNote                  |     no    |   no   |     no     |     -    |       no
 * AssetInventory*           |    yes    |  yes   |     no     |    no    | yes (other area)
 *
 * AssetInventory* = every zone in ASSET_INVENTORY_ZONE_QUERY_KEYS. Every dropzone section can
 * move files into the asset inventory upload areas; moves back out target the client dropzone or
 * the counselor zone, and files also move between different asset inventory areas (same-area
 * drops stay forbidden). Nothing ever moves INTO ClientSelf — it is the client-portal staging
 * area. The asset inventory targets derive from ASSET_INVENTORY_ZONE_QUERY_KEYS so a new upload
 * area extends this matrix automatically.
 */
const ALLOWED_DROPS: Record<DocumentGroup, Set<DocumentGroup>> = {
  [DocumentGroup.Counselor]: new Set([DocumentGroup.Client, ...assetInventoryZones]),
  [DocumentGroup.Client]: new Set([DocumentGroup.Counselor, ...assetInventoryZones]),
  [DocumentGroup.ClientSelf]: new Set([DocumentGroup.Counselor, DocumentGroup.Client, ...assetInventoryZones]),
  [DocumentGroup.CaseNote]: new Set(),
  ...ASSET_INVENTORY_DROPS,
};

export function isDropAllowed(source: DocumentGroup, target: DocumentGroup): boolean {
  return ALLOWED_DROPS[source].has(target);
}
